import React from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';
import { isReadable, useAutoTransitioningColors } from '@atlassian/color-picker';
import { useProductDependencies } from '@atlassian/company-hub-common';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';

import type { LinksMenuExtensionActions } from '../../../controllers/use-links-menu-parameters-and-actions';
import type { LinksMenuParameters } from '../../../types';
import { AlignmentConfig } from './alignment-config';
import { BackgroundConfig } from './background-config';
import { TextConfig } from './text-config';

const stylesTabStyles = xcss({
	width: '100%',
});

export type StylesTabProps = {
	actions: LinksMenuExtensionActions;
	parameters: LinksMenuParameters;
};

export const StylesTab = ({ parameters, actions }: StylesTabProps): React.JSX.Element => {
	const { styles } = parameters ?? {};
	const {
		updateBorder,
		updateBorderColor,
		updateCornerRadius,
		updateCornerStyle,
		updateFillColor,
		updateHighlightColor,
		updateTextColor,
	} = actions;

	// Show the colours the canvas actually paints: the menu re-derives an
	// auto-managed black/white against its fill at render time. Same inputs as
	// `links-menu-content`.
	const resolveAutoTextColor = useHubAutoTextColor('links-menu-extension');
	const isCanvasParityEnabled = fg('hubs_config_panel_text_color_parity');
	const resolveTextColor = (textColor: string) =>
		isCanvasParityEnabled
			? (resolveAutoTextColor({
					backgroundColor: styles.fillColor,
					textColor,
					defaultRef: 'primary.200',
				}) ?? textColor)
			: textColor;
	const displayedTextColor = resolveTextColor(styles.textColor);
	const displayedHighlightColor = resolveTextColor(styles.highlightColor);

	const isReadableFillColor = (color: string) => isReadable(color, displayedTextColor);
	const isReadableTextColor = (color: string) => isReadable(color, styles.fillColor);

	const { branding } = useProductDependencies();

	useAutoTransitioningColors(
		styles.fillColor,
		isReadableTextColor,
		styles.textColor,
		updateTextColor,
		branding?.defaultParameters,
	);

	return (
		<Flex direction="column" xcss={stylesTabStyles}>
			<AlignmentConfig
				updateAlignment={actions.updateAlignment}
				alignment={parameters.styles.alignment}
			/>
			<BackgroundConfig
				border={styles.border}
				updateBorder={updateBorder}
				borderColor={styles.borderColor}
				updateBorderColor={updateBorderColor}
				fillColor={styles.fillColor}
				updateFillColor={updateFillColor}
				cornerStyle={styles.cornerStyle}
				updateCornerStyle={updateCornerStyle}
				cornerRadius={styles.cornerRadius}
				updateCornerRadius={updateCornerRadius}
				isReadableFillColor={isReadableFillColor}
			/>
			<TextConfig
				highlightColor={displayedHighlightColor}
				updateHighlightColor={updateHighlightColor}
				textColor={displayedTextColor}
				updateTextColor={updateTextColor}
				isReadableTextColor={isReadableTextColor}
			/>
		</Flex>
	);
};
